// Fonts for Settings → Appearance → Fonts. Bundled with the app (no third-party font
// servers, so the CSP stays 'self') and loaded only when chosen: each import becomes its
// own small CSS + woff2 chunk, and the browser fetches only the character sets it needs.

export interface FontChoice {
  id: string;
  name: string;
  kind: 'Sans serif' | 'Serif' | 'Display' | 'Handwriting';
  /** CSS font-family list, with fallbacks. */
  family: string;
  load?: () => Promise<unknown>;
}

const SANS = "'Segoe UI', system-ui, -apple-system, Roboto, sans-serif";
const SERIF = "Georgia, 'Times New Roman', serif";

export const FONTS: FontChoice[] = [
  { id: 'system', name: 'System default', kind: 'Sans serif', family: SANS },
  { id: 'inter', name: 'Inter', kind: 'Sans serif', family: `'Inter Variable', ${SANS}`, load: () => import('@fontsource-variable/inter/wght.css') },
  { id: 'roboto', name: 'Roboto', kind: 'Sans serif', family: `'Roboto Variable', ${SANS}`, load: () => import('@fontsource-variable/roboto/wght.css') },
  { id: 'open-sans', name: 'Open Sans', kind: 'Sans serif', family: `'Open Sans Variable', ${SANS}`, load: () => import('@fontsource-variable/open-sans/wght.css') },
  {
    id: 'lato',
    name: 'Lato',
    kind: 'Sans serif',
    family: `'Lato', ${SANS}`,
    load: () => Promise.all([import('@fontsource/lato/300.css'), import('@fontsource/lato/400.css'), import('@fontsource/lato/700.css')]),
  },
  { id: 'nunito', name: 'Nunito', kind: 'Sans serif', family: `'Nunito Variable', ${SANS}`, load: () => import('@fontsource-variable/nunito/wght.css') },
  { id: 'manrope', name: 'Manrope', kind: 'Sans serif', family: `'Manrope Variable', ${SANS}`, load: () => import('@fontsource-variable/manrope/wght.css') },
  { id: 'montserrat', name: 'Montserrat', kind: 'Sans serif', family: `'Montserrat Variable', ${SANS}`, load: () => import('@fontsource-variable/montserrat/wght.css') },
  {
    id: 'poppins',
    name: 'Poppins',
    kind: 'Sans serif',
    family: `'Poppins', ${SANS}`,
    load: () =>
      Promise.all([
        import('@fontsource/poppins/300.css'),
        import('@fontsource/poppins/400.css'),
        import('@fontsource/poppins/500.css'),
        import('@fontsource/poppins/600.css'),
        import('@fontsource/poppins/700.css'),
      ]),
  },
  { id: 'raleway', name: 'Raleway', kind: 'Sans serif', family: `'Raleway Variable', ${SANS}`, load: () => import('@fontsource-variable/raleway/wght.css') },
  { id: 'merriweather', name: 'Merriweather', kind: 'Serif', family: `'Merriweather Variable', ${SERIF}`, load: () => import('@fontsource-variable/merriweather/wght.css') },
  { id: 'lora', name: 'Lora', kind: 'Serif', family: `'Lora Variable', ${SERIF}`, load: () => import('@fontsource-variable/lora/wght.css') },
  {
    id: 'playfair',
    name: 'Playfair Display',
    kind: 'Display',
    family: `'Playfair Display Variable', ${SERIF}`,
    load: () => import('@fontsource-variable/playfair-display/wght.css'),
  },
  { id: 'oswald', name: 'Oswald', kind: 'Display', family: `'Oswald Variable', ${SANS}`, load: () => import('@fontsource-variable/oswald/wght.css') },
  { id: 'bebas', name: 'Bebas Neue', kind: 'Display', family: `'Bebas Neue', ${SANS}`, load: () => import('@fontsource/bebas-neue/400.css') },
  { id: 'caveat', name: 'Caveat', kind: 'Handwriting', family: `'Caveat Variable', cursive`, load: () => import('@fontsource-variable/caveat/wght.css') },
];

export const fontById = (id: string): FontChoice => FONTS.find((f) => f.id === id) ?? FONTS[0]!;

const loaded = new Map<string, Promise<unknown>>();

/** Loads a font once per session. Failures fall back to the next font in the family list. */
export function loadFont(id: string): Promise<unknown> {
  const f = fontById(id);
  if (!f.load) return Promise.resolve();
  let p = loaded.get(f.id);
  if (!p) {
    p = f.load().catch(() => loaded.delete(f.id));
    loaded.set(f.id, p);
  }
  return p;
}
